import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const QuickExplainerSection = ({ currentLanguage }) => {
  const navigate = useNavigate();

  const content = {
    EN: {
      title: "How it works in 3 simple steps",
      steps: [
        {
          icon: "UserPlus",
          title: "Apply online in minutes",
          description: "Quick and easy application process with instant eligibility check."
        },
        {
          icon: "CreditCard",
          title: "Get your reloadable prepaid card",
          description: "Receive your PSPXpress Mastercard® Prepaid Card and start using it right away."
        },
        {
          icon: "BarChart3",
          title: "Activity may be reported to Equifax",
          description: "Your responsible card usage may help build your credit history."
        }
      ],
      learnMore: "Learn More"
    },
    FR: {
      title: "Comment ça marche en 3 étapes simples",
      steps: [
        {
          icon: "UserPlus",
          title: "Postulez en ligne en quelques minutes",
          description: "Processus de demande rapide et facile avec vérification d'admissibilité instantanée."
        },
        {
          icon: "CreditCard",
          title: "Obtenez votre carte prépayée rechargeable",
          description: "Recevez votre carte Mastercard® prépayée PSPXpress et commencez à l\'utiliser immédiatement."
        },
        {
          icon: "BarChart3",
          title: "L'activité peut être signalée à Equifax",
          description: "Votre utilisation responsable de la carte peut aider à construire votre historique de crédit."
        }
      ],
      learnMore: "Apprendre encore plus"
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleLearnMore = () => {
    navigate('/how-it-works');
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {text?.title}
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-12"
        >
          {text?.steps?.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="text-center group"
            >
              {/* Step Number */}
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary transition-colors duration-300">
                  <Icon name={step?.icon} size={32} className="text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {step?.title}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {step?.description}
              </p>

              {/* Connector Arrow (except for last step) */}
              {index < text?.steps?.length - 1 && (
                <div className="hidden md:block absolute top-8 left-full transform -translate-y-1/2 -translate-x-8">
                  <Icon name="ArrowRight" size={24} className="text-gray-300" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Button
            variant="outline"
            size="lg"
            onClick={handleLearnMore}
            className="font-semibold px-8 py-4"
            iconName="ArrowRight"
            iconPosition="right"
          >
            {text?.learnMore}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default QuickExplainerSection;